// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { COMP_OFF_WORKED_ON_TYPES, HALF_DAY_PARTS } from '../enums.js';
import { booleanQuerySchema, isoDateSchema, paginationQuerySchema, uuidSchema } from '../common.js';
// Leave v2 (HR portal Prompt 7): balances, allocations, year close, the comment thread, comp-off, team views. Every figure
// of a balance is computed by the domain's computeLeaveBalances (packages/domain/src/leave/balances.ts) — no counters.
const halfDaysSchema = (min, max) => z.number().min(min).max(max).multipleOf(0.5);
const yearSchema = z.coerce.number().int().min(2000).max(2100);
const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected yyyy-MM');
export const leaveBalancesQuerySchema = paginationQuerySchema.extend({
    year: yearSchema.optional(),
    employeeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
});
// ----- allocations ---------------------------------------------------------------------------------------------------------
export const leaveAllocationListQuerySchema = paginationQuerySchema.extend({
    year: yearSchema,
    employeeId: uuidSchema.optional(),
    leaveTypeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
});
/** One allocation row to create or replace (PUT = full row; omitted optional numbers are 0 / null). */
export const leaveAllocationRowSchema = z.object({
    employeeId: uuidSchema,
    leaveTypeId: uuidSchema,
    year: yearSchema,
    allocatedDays: halfDaysSchema(0, 366),
    carriedForwardDays: halfDaysSchema(0, 366).optional(),
    carriedForwardExpiresOn: isoDateSchema.nullable().optional(),
    openingBalanceDays: halfDaysSchema(-366, 366).optional(),
    adjustmentDays: halfDaysSchema(-366, 366).optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
}).superRefine((v, ctx) => {
    if (v.carriedForwardExpiresOn && !(v.carriedForwardDays && v.carriedForwardDays > 0))
        ctx.addIssue({ code: 'custom', path: ['carriedForwardExpiresOn'], message: 'An expiry needs carried-forward days' });
});
export const leaveAllocationUpsertSchema = z.object({ rows: z.array(leaveAllocationRowSchema).min(1).max(500) });
/** Create the missing rows of a year from the types' yearly allowance for every active employee (prorated for joiners). Existing rows are never overwritten. */
export const leaveAllocationGenerateSchema = z.object({
    year: yearSchema,
    leaveTypeIds: z.array(uuidSchema).min(1).max(50).optional(),
});
export const leaveYearCloseSchema = z.object({ fromYear: yearSchema });
// ----- comment thread ------------------------------------------------------------------------------------------------------
export const leaveCommentInputSchema = z.object({ body: z.string().trim().min(1).max(2000) });
// ----- self-service v2 -----------------------------------------------------------------------------------------------------
/** PATCH /orgs/:orgId/me/leave/:id — edit a PENDING / INFO_REQUESTED request (omitted = unchanged; no defaults). */
export const selfLeaveEditSchema = z.object({
    leaveTypeId: uuidSchema.optional(),
    startDate: isoDateSchema.optional(),
    endDate: isoDateSchema.optional(),
    isHalfDay: z.boolean().optional(),
    halfDayPart: z.enum(HALF_DAY_PARTS).nullable().optional(),
    reason: z.string().trim().min(3, 'Tell HR briefly why').max(1000).optional(),
    /** Workforce controls §4: the explanation for a late / short-notice / emergency request (null clears it). */
    explanation: z.string().trim().max(1000).nullable().optional(),
    /** Workforce controls §4: the employee's emergency mark (omitted = unchanged). */
    isEmergency: z.boolean().optional(),
});
/**
 * POST …/me/leave/:id/withdraw — the reason is required (Finance B-98; it reaches the approvers and the timeline). The
 * pre-v2 POST …/me/leave/:id/cancel takes no body and records "Withdrawn by the requester".
 */
export const selfLeaveWithdrawSchema = z.object({ reason: z.string().trim().min(3, 'Tell your approver why').max(500) });
/** POST …/me/leave/:id/reply — answer the approver's question; the request goes back to PENDING. */
export const selfLeaveReplySchema = z.object({ body: z.string().trim().min(1).max(2000) });
// ----- comp-off ------------------------------------------------------------------------------------------------------------
export const selfCompOffRequestSchema = z.object({
    workedOn: isoDateSchema,
    workedOnType: z.enum(COMP_OFF_WORKED_ON_TYPES),
    workedMinutes: z.number().int().min(1).max(1440),
    location: z.string().trim().min(1).max(200),
    summary: z.string().trim().min(1).max(1000),
});
export const selfCompOffPreviewQuerySchema = z.object({ workedOn: isoDateSchema });
export const leaveCalendarQuerySchema = z.object({
    month: monthSchema,
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    includePending: booleanQuerySchema.default(true),
});
//# sourceMappingURL=leave.js.map